/**
 * Brinde (gift) on add-to-cart.
 *
 * When a product belonging to a collection that has the `custom.brinde` metafield
 * (a product reference) is added to the cart, the referenced gift product is added
 * to the cart drawer automatically.
 *
 * The eligible gift variant is rendered server-side by `snippets/brinde-variant.liquid`
 * onto each add-to-cart form as `data-brinde-variant`. This script listens for the
 * theme's `product-ajax:added` event, resolves the gift for the added product and
 * adds it once, then refreshes the cart drawer.
 *
 * On every `cart:updated`, it reconciles: if no remaining cart line points to a gift
 * (each line carries `data-brinde-variant`; gift lines are flagged `data-brinde-gift`),
 * the orphan gift line is removed.
 *
 * Dedupe is done from the rendered cart DOM (`data-quantity-variant-id`) instead of
 * `/cart.js`, because a 3rd-party app on this store intercepts `/cart/*` and breaks
 * the JSON cart endpoint.
 */
(function () {
  'use strict';

  var PROP_KEY = '_brinde';
  var inFlight = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // True if a line for this variant is already present in the rendered cart/drawer.
  // Uses the line-root `data-variant-id` (present on every cart line) because the gift
  // line renders a locked quantity without the editable `data-quantity-variant-id`.
  function giftInCartDOM(variantId) {
    return !!document.querySelector('[data-cart-item][data-variant-id="' + variantId + '"]');
  }

  function removeLine(key) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function (r) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return r;
    });
  }

  // Remove gift lines whose triggering product is no longer in the cart.
  // Each rendered cart line carries `data-brinde-variant` (the gift it would add) and
  // gift lines are flagged with `data-brinde-gift` (via the `_brinde` property).
  function reconcileGifts() {
    if (inFlight) return;
    var lines = document.querySelectorAll('[data-cart-item]');
    if (!lines.length) return;

    var required = {};
    lines.forEach(function (l) {
      if (l.hasAttribute('data-brinde-gift')) return;
      var b = l.getAttribute('data-brinde-variant');
      if (b) required[b] = true;
    });

    var orphan = null;
    lines.forEach(function (l) {
      if (orphan) return;
      if (!l.hasAttribute('data-brinde-gift')) return;
      var v = l.getAttribute('data-variant-id');
      if (!required[v]) orphan = l;
    });

    if (!orphan) return;
    var key = orphan.getAttribute('data-line-key');
    if (!key) return;

    inFlight = true;
    removeLine(key)
      .catch(function (err) {
        console.warn('[brinde] remove', err);
      })
      .finally(function () {
        inFlight = false;
      });
  }

  document.addEventListener('cart:updated', reconcileGifts);

  function addGift(variantId) {
    var body = {
      items: [{ id: variantId, quantity: 1, properties: { [PROP_KEY]: 'true' } }]
    };
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/add.js ' + r.status);
        return r.json();
      })
      .then(function (data) {
        // Let the theme's cart components re-fetch and re-render the drawer/page.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return data;
      });
  }

  document.addEventListener('product-ajax:added', function (e) {
    if (inFlight) return;

    var added = e && e.detail && e.detail.product;
    if (!added) return;

    var productId =
      added.product_id || (added.items && added.items[0] && added.items[0].product_id);
    if (!productId) return;

    var form = document.querySelector(
      '[data-product-id="' + productId + '"][data-brinde-variant]'
    );
    if (!form) return;

    var giftVariant = parseInt(form.getAttribute('data-brinde-variant'), 10);
    if (!giftVariant) return;

    // Don't re-trigger when the added product IS the gift itself.
    var addedVariant = added.variant_id || added.id;
    if (addedVariant === giftVariant) return;

    // Skip if the gift is already in the cart.
    if (giftInCartDOM(giftVariant)) return;

    inFlight = true;
    addGift(giftVariant)
      .catch(function (err) {
        console.warn('[brinde]', err);
      })
      .finally(function () {
        inFlight = false;
      });
  });
})();
